import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";

/**
 * AdminNoGpsSessions — audit clocked-in sessions that have no GPS
 * coords stamped at clock-in. Usually results from:
 *   • Admin override at clock-in time
 *   • `location_gate_disabled: true` on the job
 *   • Legacy record pre-dating the location gate
 *
 * Provides one-click force-clock-out per row so dispatchers can clean
 * up ghost sessions that would otherwise pin techs to old WOs forever.
 */
export default function AdminNoGpsSessions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/no-gps-sessions", { params: { include_closed: includeClosed } });
      setRows(r.data.sessions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeClosed]);

  const doForce = async (id, wo) => {
    if (!window.confirm(`Force clock-out for WO ${wo}?\n\nAdds an audit log entry noting this session had no GPS record.`)) return;
    setBusyId(id);
    try {
      await api.post(`/admin/no-gps-sessions/${id}/force-clockout`);
      toast.success(`✓ Force-closed WO ${wo}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Force clock-out failed");
    } finally { setBusyId(null); }
  };

  const reason = (r) => {
    if (r.location_gate_disabled) return "Job had location gate disabled";
    if (r.status === "completed") return "Legacy — pre-gate";
    return "Admin override or missing GPS at clock-in";
  };

  return (
    <AdminShell>
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        <div className="mb-4">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Timekeeping · Audit</div>
          <h1 className="font-display font-black text-2xl text-[#0f172a]">Sessions With No GPS</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Techs currently (or historically) clocked in without a GPS record. Usually harmless — but a stuck
            "phantom" session can hide new WOs from a tech until you close it out.
          </p>
        </div>

        <div className="flex items-center gap-3 mb-3">
          <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer" data-testid="no-gps-include-closed">
            <input type="checkbox" checked={includeClosed} onChange={e => setIncludeClosed(e.target.checked)}
              className="w-4 h-4"/>
            Include closed / historical sessions
          </label>
          <button onClick={load} data-testid="no-gps-refresh"
            className="ml-auto h-9 px-3 bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest">
            Refresh
          </button>
        </div>

        {loading && <div className="p-8 text-center text-sm text-slate-500" data-testid="no-gps-loading">Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="p-8 text-center bg-white border-2 border-emerald-200 rounded-xl" data-testid="no-gps-empty">
            <div className="text-4xl mb-2">✅</div>
            <div className="font-display font-black text-lg text-emerald-800">
              {includeClosed ? "Nothing in the whole history" : "No stuck sessions"}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Every currently-active session has a GPS stamp.
            </div>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="bg-white border-2 border-slate-200 rounded-xl overflow-hidden" data-testid="no-gps-table">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b-2 border-slate-200 text-[9px] font-black uppercase tracking-widest text-slate-500">
              <div className="col-span-2">WO</div>
              <div className="col-span-3">Property</div>
              <div className="col-span-2">Tech</div>
              <div className="col-span-2">Clocked In</div>
              <div className="col-span-2">Why</div>
              <div className="col-span-1 text-right">Action</div>
            </div>
            {rows.map(r => (
              <div key={r.id} className="grid grid-cols-12 gap-2 px-3 py-3 border-b border-slate-100 items-center text-sm hover:bg-amber-50/50"
                   data-testid={`no-gps-row-${r.id}`}>
                <div className="col-span-2">
                  <Link to={`/admin/jobs/${r.id}`} className="text-[#00C4CC] hover:underline font-black">
                    WO {r.work_order_number || r.id.slice(-4)}
                  </Link>
                  {r.status && (
                    <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">{r.status}</div>
                  )}
                </div>
                <div className="col-span-3 truncate">
                  <div className="font-semibold truncate">{r.property_address || "—"}</div>
                  {r.unit && <div className="text-xs text-slate-500">Unit {r.unit}</div>}
                </div>
                <div className="col-span-2 truncate">
                  <div className="font-semibold truncate">{r.assigned_to_name || "Unassigned"}</div>
                </div>
                <div className="col-span-2 text-xs text-slate-700 font-mono">
                  {r.clock_in_at ? new Date(r.clock_in_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                </div>
                <div className="col-span-2 text-[10px] text-amber-800 font-bold">{reason(r)}</div>
                <div className="col-span-1 text-right">
                  {!r.clock_out_at && (
                    <button onClick={() => doForce(r.id, r.work_order_number || r.id.slice(-4))}
                      disabled={busyId === r.id}
                      data-testid={`no-gps-force-${r.id}`}
                      className="h-8 px-2 bg-red-600 hover:bg-red-700 text-white text-[10px] font-black uppercase tracking-widest rounded disabled:opacity-50">
                      {busyId === r.id ? "…" : "Force Out"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminShell>
  );
}
